import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Box,
  useTheme,
  ListItemIcon
} from "@mui/material"
import { DragIndicator, Close } from "@mui/icons-material"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import AppButton from "components/StyledComponents/AppButton"
import { useHistory } from "react-router-dom"
import ToggleSwitch from "components/StyledComponents/ToggleSwitch"

const RearrangeModal = ({ open, onClose, sections, onSave }) => {
  const [localSections, setLocalSections] = useState([])
  const theme = useTheme()
  const history = useHistory()

  useEffect(() => {
    if (open) {
      setLocalSections([...sections])
    }
  }, [open, sections])

  const onDragEnd = (result) => {
    if (!result.destination) return
    const items = Array.from(localSections)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)
    setLocalSections(items)
  }

  const handleToggle = (id) => {
    setLocalSections(
      localSections.map((s) => (s.id === id ? { ...s, is_active: !s.is_active } : s))
    )
  }

  const handleSave = () => {
    onSave(localSections)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "12px",
          backgroundImage: "none"
        }
      }}>
      <DialogTitle
        sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Rearrange</Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: theme.palette.text.primary,
            position: "absolute",
            top: "15px",
            right: "15px",
            zIndex: 10,
            backgroundColor: theme.palette.background.main
          }}
          size="small">
          <Close sx={{ height: 14, width: 14 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="sections">
            {(provided) => (
              <List {...provided.droppableProps} ref={provided.innerRef}>
                {localSections.map((section, index) => (
                  <Draggable key={section.id} draggableId={section.id} index={index}>
                    {(provided, snapshot) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          backgroundColor: snapshot.isDragging
                            ? theme.palette.drawer.main
                            : "transparent",
                          mb: 1,
                          borderRadius: "8px",
                          mx: 1,
                          width: "auto"
                        }}>
                        <ListItemIcon {...provided.dragHandleProps}>
                          <DragIndicator sx={{ color: theme.palette.text.secondary }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={section?.title}
                          sx={{
                            color: theme.palette.text.primary
                          }}
                        />
                        <ToggleSwitch
                          edge="end"
                          checked={section.is_active}
                          onChange={() => handleToggle(section.id)}
                          color="primary"
                        />
                      </ListItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>
      <DialogActions sx={{ p: 2, display: "flex", justifyContent: "space-between" }}>
        <AppButton
          onClick={() => history.push("/super-admin/dashboard/preshift-sections-management")}>
          Manage Sections
        </AppButton>
        <Box sx={{ display: "flex", gap: 1 }}>
          <AppButton onClick={onClose} style={{ padding: "10px 24px" }}>
            Cancel
          </AppButton>
          <AppButton onClick={handleSave} style={{ padding: "10px 24px" }}>
            Save
          </AppButton>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default RearrangeModal
